/**
 * Was das Büro am Bestand ändert — und wie daraus wieder ein Bestand wird.
 *
 * **Der Bestand bleibt unveränderlich, die Änderungen liegen daneben.** Das
 * ist dasselbe Muster wie in der Monteur-App, aber aus einem anderen Grund:
 * Dort trennt es „schon draußen" von „wartet noch", hier trennt es die
 * *Projektion* von dem, was das Büro in dieser Sitzung entschieden hat. Im
 * Zielbild wandern diese Entscheidungen an die Fachdienste — `INBOX-001` für
 * den Eingangskorb, `REPORT-001` für die Berichte — und kommen als geänderte
 * Projektion zurück (ADR-021).
 *
 * Wer sie stattdessen in den Bestand hineinschriebe, hätte im ersten Moment
 * weniger Code und beim Anschluss der echten Dienste keinen Ort, an dem der
 * Unterschied sichtbar wäre.
 *
 * **Jede Änderung erzeugt einen Auditeintrag.** `05` §5.15 stellt jeden
 * Statuswechsel unter Auditpflicht, und ADR-045 verlangt bei stellvertretendem
 * Schalten beide Namen. Das ist hier keine nachträgliche Zutat: Die Funktionen
 * geben Änderung **und** Eintrag zurück, damit man das eine nicht ohne das
 * andere haben kann.
 *
 * Reines TypeScript — kein React. Die Import-Grenze aus ADR-016 gilt zwar
 * formal nur für `contracts`, `domain` und `sync`, aber eine Zustandslogik,
 * die sich ohne Oberfläche prüfen lässt, ist auch hier die bessere Wahl: Genau
 * diese Regeln waren in der Monteur-App die, die im Browser durchfielen.
 */

import { formatResourceRef } from "@hwc/contracts";
import type { ResourceRef, Rolle } from "@hwc/contracts";
import type {
  Anschrift,
  Auditeintrag,
  Auftrag,
  Bestand,
  Berichtszustand,
  Eingangsstatus,
  Chatbeitrag,
  Kontakt,
  Kunde,
  MagicLink,
  Servicebericht,
  Wartungsplan,
} from "@hwc/demo-data";
import { ref } from "@hwc/demo-data";
import type { EinsatzZustand } from "@hwc/sync";
import { uebergangErlaubt } from "@hwc/sync";
import { darfEingangBearbeiten, darfKundenPflegen, darfSchalten } from "@hwc/auth";

/** Was in dieser Sitzung entschieden wurde. */
export interface Vorgaenge {
  /** Einsatzzustände, überschrieben. Schlüssel ist der Ref als Zeichenkette. */
  readonly einsatzzustand: Readonly<Record<string, EinsatzZustand>>;
  /** Eingangsvorgänge: Status, Zuordnung, Bearbeiter. */
  readonly eingang: Readonly<Record<string, EingangsAenderung>>;
  /** Serviceberichte: Zustand und Freigabe. */
  readonly bericht: Readonly<Record<string, BerichtsAenderung>>;
  /** Aufträge, die in dieser Sitzung entstanden sind. */
  readonly neueAuftraege: readonly Auftrag[];
  /** Kunden, die in dieser Sitzung entstanden sind. */
  readonly neueKunden: readonly Kunde[];
  /**
   * Berichte, die die Monteur-App erzeugt und übermittelt hat.
   *
   * Getrennt von `bericht`: Das ist eine Änderung an einem Bericht, den der
   * Bestand kennt. Ein Bericht aus dem Feld ist neu — er entstand beim
   * Abschluss eines Einsatzes (`07` §7.8) und hat im Demobestand kein
   * Gegenstück. Beides in einen Topf zu werfen hieße, jede Änderung als
   * potenziell vollständigen Datensatz behandeln zu müssen.
   */
  readonly neueBerichte: readonly Servicebericht[];
  /** MagicLinks, die widerrufen wurden. */
  readonly widerrufen: readonly string[];
  /** Zugänge, die in dieser Sitzung entstanden sind. */
  readonly neueLinks: readonly MagicLink[];
  /** Chatbeiträge, die in dieser Sitzung geschrieben wurden. */
  readonly neueBeitraege: readonly Chatbeitrag[];
  /** Dateien, die aus dem Papierkorb geholt wurden. */
  readonly zurueckgeholt: readonly string[];
  /** Dateien, die endgültig entfernt wurden. */
  readonly entfernt: readonly string[];
  /** Wartungspläne: Status und nächste Fälligkeit. */
  readonly wartung: Readonly<Record<string, WartungsAenderung>>;
  /** Die Auditeinträge dieser Sitzung, neueste zuletzt. */
  readonly audit: readonly Auditeintrag[];
}

export interface EingangsAenderung {
  readonly status: Eingangsstatus;
  readonly zugeordneterAuftrag?: ResourceRef;
  readonly bearbeiter: ResourceRef;
}

export interface WartungsAenderung {
  readonly status: Wartungsplan["status"];
  readonly naechsteFaelligkeit: string;
}

export interface BerichtsAenderung {
  readonly zustand: Berichtszustand;
  readonly freigegebenVon?: ResourceRef;
  readonly freigegebenAm?: string;
}

export const KEINE_VORGAENGE: Vorgaenge = {
  einsatzzustand: {},
  eingang: {},
  bericht: {},
  neueAuftraege: [],
  neueKunden: [],
  neueBerichte: [],
  widerrufen: [],
  neueLinks: [],
  neueBeitraege: [],
  zurueckgeholt: [],
  entfernt: [],
  wartung: {},
  audit: [],
};

/** Das Ergebnis eines Versuchs: entweder neue Vorgänge oder eine Absage. */
export type Ausgang =
  | { readonly ok: true; readonly vorgaenge: Vorgaenge }
  | { readonly ok: false; readonly grund: string };

let zaehler = 0;

/**
 * Eine Kennung für einen Auditeintrag.
 *
 * Fortlaufend statt zufällig: Die Einträge dieser Sitzung sollen in der
 * Reihenfolge lesbar sein, in der sie entstanden — und eine Zufallskennung
 * beantwortet die Frage „was war zuerst" nicht, wenn zwei im selben
 * Millisekundenfenster liegen.
 */
function auditKennung(): string {
  zaehler += 1;
  return `au-sitzung-${String(zaehler).padStart(4, "0")}`;
}

function eintrag(args: {
  objekt: ResourceRef;
  handlung: string;
  akteur: ResourceRef;
  imAuftragVon?: ResourceRef | undefined;
  von?: string | undefined;
  nach?: string | undefined;
}): Auditeintrag {
  return {
    ref: ref("portal", "audit-entry", auditKennung()),
    zeitpunkt: new Date().toISOString(),
    objekt: args.objekt,
    handlung: args.handlung,
    akteur: args.akteur,
    ...(args.imAuftragVon ? { imAuftragVon: args.imAuftragVon } : {}),
    ...(args.von ? { von: args.von } : {}),
    ...(args.nach ? { nach: args.nach } : {}),
  };
}

// ───────────────────────── Einsatzzustand ─────────────────────────

/**
 * Schaltet einen Einsatz — mit **beiden** Prüfungen.
 *
 * `uebergangErlaubt` fragt, ob der Weg fachlich existiert; `darfSchalten`
 * fragt, ob dieser Benutzer ihn gehen darf. Beide Antworten werden gebraucht,
 * und die Reihenfolge ist nicht beliebig: Erst die Rechte, dann die Fachlogik.
 * Wer nicht darf, bekommt keine Auskunft darüber, was fachlich ginge.
 */
export function schalteEinsatz(args: {
  vorgaenge: Vorgaenge;
  einsatzRef: ResourceRef;
  von: EinsatzZustand;
  nach: EinsatzZustand;
  akteur: ResourceRef;
  rollen: readonly Rolle[];
  /** Der zugewiesene Monteur, falls es einen gibt. */
  zugewiesen?: ResourceRef | undefined;
}): Ausgang {
  const istZugewiesen =
    args.zugewiesen !== undefined
    && formatResourceRef(args.zugewiesen) === formatResourceRef(args.akteur);

  const recht = darfSchalten({
    von: args.von,
    nach: args.nach,
    rollen: args.rollen,
    istZugewiesen,
  });

  if (recht === "nein") {
    return {
      ok: false,
      grund: `Diese Rolle darf ${args.von} → ${args.nach} nicht schalten.`,
    };
  }

  if (!uebergangErlaubt(args.von, args.nach)) {
    return {
      ok: false,
      grund: `${args.von} → ${args.nach} ist kein gültiger Übergang.`,
    };
  }

  const schluessel = formatResourceRef(args.einsatzRef);

  return {
    ok: true,
    vorgaenge: {
      ...args.vorgaenge,
      einsatzzustand: { ...args.vorgaenge.einsatzzustand, [schluessel]: args.nach },
      audit: [
        ...args.vorgaenge.audit,
        eintrag({
          objekt: args.einsatzRef,
          // Die Handlung sagt, **wie** geschaltet wurde. ADR-045: Der Preis
          // des stellvertretenden Schaltens ist die Urheberschaft, und die
          // muss im Protokoll stehen, nicht nur in der Rechtetabelle.
          handlung:
            recht === "stellvertretend"
              ? "Zustand stellvertretend gewechselt"
              : "Zustand gewechselt",
          akteur: args.akteur,
          imAuftragVon: recht === "stellvertretend" ? args.zugewiesen : undefined,
          von: args.von,
          nach: args.nach,
        }),
      ],
    },
  };
}

// ───────────────────────── Eingangskorb ─────────────────────────

/**
 * Ordnet einen Eingangsvorgang einem Auftrag zu.
 *
 * **Quarantäne bleibt außen vor.** Ein Vorgang, dessen Anhang der
 * Signaturscanner beanstandet hat (ADR-023), wird nicht durch eine Zuordnung
 * still zum normalen Vorgang. Ihn freizugeben ist eine eigene Entscheidung mit
 * eigener Rolle — `darfQuarantaeneFreigeben` verlangt den Administrator.
 */
export function ordneZu(args: {
  vorgaenge: Vorgaenge;
  vorgangRef: ResourceRef;
  status: Eingangsstatus;
  auftrag?: ResourceRef | undefined;
  bearbeiter: ResourceRef;
  bisher: Eingangsstatus;
  rollen: readonly Rolle[];
}): Ausgang {
  /*
   * **Die Rechteprüfung stand nur in der Oberfläche.**
   *
   * `Eingang.tsx` fragt `darfEingangBearbeiten` und blendet die Knöpfe aus —
   * das ist richtig, aber es ist keine Berechtigung. Aufgefallen am
   * 05.09.2026 beim Schreiben der ersten Prüfungen für diese Datei:
   * `schalteEinsatz` und `legeKundenAn` fragen die Rolle, `ordneZu` nicht.
   *
   * Eine ausgeblendete Schaltfläche schützt genau so lange, wie niemand die
   * Funktion von woanders aufruft — und „woanders" ist hier der Dienst, den
   * `INBOX-001` aus dieser Logik baut.
   */
  if (!darfEingangBearbeiten(args.rollen)) {
    return {
      ok: false,
      grund:
        "Ihre Rolle bearbeitet den Eingangskorb nicht. Nach `07` §7.2 liegt "
        + "er bei Büro und Disposition.",
    };
  }

  if (args.bisher === "quarantaene") {
    return {
      ok: false,
      grund:
        "Der Vorgang steht in Quarantäne. Eine Freigabe kann nur der "
        + "Administrator veranlassen (ADR-023).",
    };
  }
  if (args.status === "zugeordnet" && !args.auftrag) {
    return { ok: false, grund: "Zum Zuordnen fehlt der Auftrag." };
  }

  const schluessel = formatResourceRef(args.vorgangRef);

  const handlung: Record<Eingangsstatus, string> = {
    neu: "Eingangsvorgang zurück in den Korb gelegt",
    in_pruefung: "Eingangsvorgang in Prüfung genommen",
    zugeordnet: "Eingangsvorgang zugeordnet",
    zurueckgestellt: "Eingangsvorgang zurückgestellt",
    abgewiesen: "Eingangsvorgang abgewiesen",
    quarantaene: "Eingangsvorgang in Quarantäne gestellt",
  };

  return {
    ok: true,
    vorgaenge: {
      ...args.vorgaenge,
      eingang: {
        ...args.vorgaenge.eingang,
        [schluessel]: {
          status: args.status,
          ...(args.auftrag ? { zugeordneterAuftrag: args.auftrag } : {}),
          bearbeiter: args.bearbeiter,
        },
      },
      audit: [
        ...args.vorgaenge.audit,
        eintrag({
          objekt: args.vorgangRef,
          handlung: handlung[args.status],
          akteur: args.bearbeiter,
          von: args.bisher,
          nach: args.status,
        }),
      ],
    },
  };
}

// ───────────────────────── Servicebericht ─────────────────────────

/**
 * Die Zustände, die das Büro von `zur_pruefung` aus setzen darf.
 *
 * `07` §7.16 nennt fünf Zustände und sagt: „Die Freigabe bezieht sich immer
 * auf eine konkrete Version." Eine Rückgabe ist kein eigener Berichtszustand
 * — sie setzt den Bericht zurück auf `entwurf` und den Einsatz auf
 * `abschluss`, und beides gehört zusammen.
 */
export function entscheideBericht(args: {
  vorgaenge: Vorgaenge;
  bericht: Servicebericht;
  entscheidung: "freigeben" | "veroeffentlichen" | "zurueckgeben";
  akteur: ResourceRef;
  rollen: readonly Rolle[];
}): Ausgang {
  // Die Rechteprüfung liegt beim Aufrufer (`darfBerichtFreigeben`), damit die
  // Oberfläche den Knopf gar nicht erst anbietet. Hier steht sie noch einmal,
  // weil eine Regel, die nur die Oberfläche kennt, keine Regel ist.
  if (!args.rollen.some((r) => r === "office" || r === "dispatcher" || r === "tenant-admin")) {
    return { ok: false, grund: "Nur das Büro gibt einen Servicebericht frei." };
  }

  const erlaubt: Record<Berichtszustand, readonly Berichtszustand[]> = {
    entwurf: ["zur_pruefung"],
    zur_pruefung: ["freigegeben", "entwurf"],
    freigegeben: ["veroeffentlicht"],
    veroeffentlicht: ["ersetzt"],
    ersetzt: [],
  };

  const ziel: Berichtszustand =
    args.entscheidung === "freigeben"
      ? "freigegeben"
      : args.entscheidung === "veroeffentlichen"
        ? "veroeffentlicht"
        : "entwurf";

  if (!erlaubt[args.bericht.zustand].includes(ziel)) {
    return {
      ok: false,
      grund: `Ein Bericht im Zustand ${args.bericht.zustand} kann nicht ${ziel} werden.`,
    };
  }

  const jetzt = new Date().toISOString();
  const schluessel = formatResourceRef(args.bericht.ref);

  return {
    ok: true,
    vorgaenge: {
      ...args.vorgaenge,
      bericht: {
        ...args.vorgaenge.bericht,
        [schluessel]: {
          zustand: ziel,
          ...(ziel === "freigegeben"
            ? { freigegebenVon: args.akteur, freigegebenAm: jetzt }
            : {}),
        },
      },
      audit: [
        ...args.vorgaenge.audit,
        eintrag({
          objekt: args.bericht.ref,
          handlung:
            args.entscheidung === "zurueckgeben"
              ? `Servicebericht Version ${args.bericht.version} zurückgegeben`
              : args.entscheidung === "freigeben"
                ? `Servicebericht Version ${args.bericht.version} freigegeben`
                : `Servicebericht Version ${args.bericht.version} veröffentlicht`,
          akteur: args.akteur,
          von: args.bericht.zustand,
          nach: ziel,
        }),
      ],
    },
  };
}

// ───────────────────────── MagicLink ─────────────────────────

export function widerrufeLink(args: {
  vorgaenge: Vorgaenge;
  linkRef: ResourceRef;
  akteur: ResourceRef;
}): Ausgang {
  const schluessel = formatResourceRef(args.linkRef);
  if (args.vorgaenge.widerrufen.includes(schluessel)) {
    return { ok: false, grund: "Dieser Zugang ist bereits widerrufen." };
  }
  return {
    ok: true,
    vorgaenge: {
      ...args.vorgaenge,
      widerrufen: [...args.vorgaenge.widerrufen, schluessel],
      audit: [
        ...args.vorgaenge.audit,
        eintrag({
          objekt: args.linkRef,
          handlung: "MagicLink widerrufen",
          akteur: args.akteur,
        }),
      ],
    },
  };
}

// ───────────────────────── Neuer Auftrag ─────────────────────────

/**
 * Legt einen Auftrag an.
 *
 * Die Nummer wird fortlaufend aus der höchsten vorhandenen gebildet. Das ist
 * für einen Demonstrationsstand richtig und für den Betrieb zu wenig: Dort
 * vergibt sie die Datenbank, und zwei gleichzeitige Anlagen dürfen nicht
 * dieselbe bekommen. Der Kommentar steht hier, damit niemand die Schleife für
 * eine Nummernvergabe hält.
 */
export function legeAuftragAn(args: {
  vorgaenge: Vorgaenge;
  bestand: Bestand;
  titel: string;
  kunde: ResourceRef;
  verantwortlich: ResourceRef;
  gewerk?: string | undefined;
  beschreibung?: string | undefined;
}): Ausgang {
  if (!args.titel.trim()) {
    return { ok: false, grund: "Ein Auftrag ohne Titel ist später nicht wiederzufinden." };
  }

  const nummern = [
    ...args.bestand.auftraege.map((a) => Number(a.nummer)),
    ...args.vorgaenge.neueAuftraege.map((a) => Number(a.nummer)),
  ].filter((n) => Number.isFinite(n));
  const naechste = String(Math.max(0, ...nummern) + 1);

  const auftrag: Auftrag = {
    ref: ref("vikunja", "project", `a-${naechste}`),
    nummer: naechste,
    titel: args.titel.trim(),
    kunde: args.kunde,
    status: "neu",
    verantwortlich: args.verantwortlich,
    angelegt: new Date().toISOString().slice(0, 10),
    ...(args.gewerk ? { gewerk: args.gewerk } : {}),
    ...(args.beschreibung ? { beschreibung: args.beschreibung } : {}),
  };

  return {
    ok: true,
    vorgaenge: {
      ...args.vorgaenge,
      neueAuftraege: [...args.vorgaenge.neueAuftraege, auftrag],
      audit: [
        ...args.vorgaenge.audit,
        eintrag({
          objekt: auftrag.ref,
          handlung: `Auftrag ${naechste} angelegt`,
          akteur: args.verantwortlich,
        }),
      ],
    },
  };
}



/**
 * Erstellt einen befristeten Zugang — `04` §4.9.
 *
 * **Sieben Tage sind die Vorgabe, und länger als vierzehn geht nicht.**
 * `05` §5.7 setzt die Sieben-Tage-Regel, und `07` §7.2 nennt es „wesentlich",
 * sie nicht auszuhöhlen: Wer dauerhaften Zugriff braucht, bekommt ein Konto
 * als `external-partner`. Eine Obergrenze, die man wegdiskutieren kann, ist
 * keine — deshalb steht sie hier und nicht nur in der Auswahlliste.
 *
 * Was diese Funktion **nicht** tut: Sie prüft keine `share`-Berechtigung beim
 * Provider, erzeugt kein Token und verschickt nichts. Das sind vier der sieben
 * Schritte aus `04` §4.9 und gehören zu `SHARE-001`.
 */
export function erstelleLink(args: {
  vorgaenge: Vorgaenge;
  ziel: ResourceRef;
  empfaenger: string;
  tage: number;
  zweck: string;
  akteur: ResourceRef;
}): Ausgang {
  if (!args.empfaenger.includes("@")) {
    return { ok: false, grund: "Ohne Empfängeradresse lässt sich der Zugang nicht binden." };
  }
  if (args.tage < 1 || args.tage > 14) {
    return {
      ok: false,
      grund:
        "Ein MagicLink läuft nach spätestens vierzehn Tagen ab (`05` §5.7). "
        + "Für dauerhaften Zugriff ist die Rolle `external-partner` gedacht.",
    };
  }

  const jetzt = new Date();
  const ablauf = new Date(jetzt.getTime() + args.tage * 86_400_000);
  const kennung = `ml-neu-${args.vorgaenge.neueLinks.length + 1}`;

  const link: MagicLink = {
    ref: ref("portal", "magiclink", kennung),
    ziel: args.ziel,
    empfaenger: args.empfaenger.trim(),
    erstellt: jetzt.toISOString(),
    laeuftAb: ablauf.toISOString(),
    widerrufen: false,
    zugriffe: 0,
    zweck: args.zweck,
  };

  return {
    ok: true,
    vorgaenge: {
      ...args.vorgaenge,
      neueLinks: [...args.vorgaenge.neueLinks, link],
      audit: [
        ...args.vorgaenge.audit,
        eintrag({
          objekt: link.ref,
          // Der Empfänger steht in der Handlung, nicht nur im Datensatz:
          // `05` §5.15 will wissen, wer Zugriff bekam, und ein Auditeintrag
          // „MagicLink erstellt" ohne Adressat beantwortet das nicht.
          handlung: `MagicLink erstellt für ${link.empfaenger}, gültig ${args.tage} Tage`,
          akteur: args.akteur,
        }),
      ],
    },
  };
}


/**
 * Schreibt einen Beitrag in den Auftragsraum — `07` §7.10.
 *
 * **Der auftragsbezogene Chat löst ein benanntes Problem**: „Kundendaten in
 * privaten Messengern auf Monteurhandys." Ein Raum je Auftrag heißt, dass die
 * Unterhaltung dort bleibt, wo sie hingehört — und nicht in einem
 * Gruppenchat, den niemand löschen kann und der beim Ausscheiden eines
 * Mitarbeiters mitgeht.
 *
 * **Kein Auditeintrag.** `05` §5.15 stellt Statuswechsel unter Auditpflicht,
 * nicht jede Nachricht. Ein Protokoll, das jeden Chatbeitrag mitschreibt, wäre
 * eine zweite Kopie der Unterhaltung an einem Ort mit anderer
 * Aufbewahrungsfrist — und würde die Trennung aus `05` §5.2 unterlaufen.
 */
export function schreibeBeitrag(args: {
  vorgaenge: Vorgaenge;
  raum: ResourceRef;
  autor: ResourceRef;
  text: string;
}): Ausgang {
  const text = args.text.trim();
  if (!text) return { ok: false, grund: "Ein leerer Beitrag hilft niemandem." };

  const beitrag: Chatbeitrag = {
    ref: ref("nextcloud", "talk-message", `tm-neu-${args.vorgaenge.neueBeitraege.length + 1}`),
    raum: args.raum,
    autor: args.autor,
    gesendet: new Date().toISOString(),
    text,
  };

  return {
    ok: true,
    vorgaenge: {
      ...args.vorgaenge,
      neueBeitraege: [...args.vorgaenge.neueBeitraege, beitrag],
    },
  };
}

// ───────────────────────── Wartungsplan ─────────────────────────

/**
 * Die vier Aktionen aus `07` §7.16 — plus das Fortsetzen eines pausierten
 * Plans, ohne das „pausieren" eine Einbahnstraße wäre.
 *
 * **Gerechnet wird in Monaten, nicht in Tagen.** Ein Intervall von sechs
 * Monaten heißt „im selben Kalendermonat wieder", nicht „in 182 Tagen": Der
 * zweite Wert wandert über die Jahre durch den Kalender, und eine
 * Heizungswartung, die nach fünf Jahren im Sommer stattfindet, hat ihren Sinn
 * verloren.
 */
export function planeWartung(args: {
  vorgaenge: Vorgaenge;
  plan: Wartungsplan;
  aktion: "pausieren" | "fortsetzen" | "verschieben" | "ueberspringen" | "nachholen";
  akteur: ResourceRef;
  /** Nur beim Verschieben: um wie viele Tage. */
  tage?: number;
}): Ausgang {
  const { plan, aktion } = args;
  const schluessel = formatResourceRef(plan.ref);
  const bisher = args.vorgaenge.wartung[schluessel];
  const status = bisher?.status ?? plan.status;
  const faellig = bisher?.naechsteFaelligkeit ?? plan.naechsteFaelligkeit;

  if (status === "beendet") {
    return { ok: false, grund: "Dieser Plan ist beendet und wird nicht mehr geändert." };
  }
  if (aktion !== "fortsetzen" && aktion !== "pausieren" && status !== "aktiv") {
    return {
      ok: false,
      grund: "Ein pausierter Plan hat keine Fälligkeit, die sich verschieben ließe.",
    };
  }

  const alsDatum = (wert: string) => new Date(`${wert}T12:00:00`);
  const alsText = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`
    + `-${String(d.getDate()).padStart(2, "0")}`;

  let neuStatus = status;
  let neuFaellig = faellig;
  let handlung: string;

  if (aktion === "pausieren") {
    neuStatus = "pausiert";
    handlung = "Wartungsplan pausiert";
  } else if (aktion === "fortsetzen") {
    neuStatus = "aktiv";
    handlung = "Wartungsplan fortgesetzt";
  } else if (aktion === "verschieben") {
    const d = alsDatum(faellig);
    d.setDate(d.getDate() + (args.tage ?? 14));
    neuFaellig = alsText(d);
    // **Einmalig.** Das Bezugsdatum bleibt unangetastet, also kommt die
    // übernächste Fälligkeit wieder im alten Rhythmus.
    handlung = `Fälligkeit einmalig verschoben um ${args.tage ?? 14} Tage`;
  } else if (aktion === "ueberspringen") {
    const d = alsDatum(faellig);
    d.setMonth(d.getMonth() + plan.intervallMonate);
    neuFaellig = alsText(d);
    handlung = "Fälligkeit übersprungen";
  } else {
    // Nachholen: jetzt fällig. Kein Sprung nach vorn — die Wartung war
    // versäumt und wird geholt, nicht vorgezogen.
    neuFaellig = alsText(new Date());
    handlung = "Versäumte Fälligkeit nachgeholt";
  }

  return {
    ok: true,
    vorgaenge: {
      ...args.vorgaenge,
      wartung: {
        ...args.vorgaenge.wartung,
        [schluessel]: { status: neuStatus, naechsteFaelligkeit: neuFaellig },
      },
      audit: [
        ...args.vorgaenge.audit,
        eintrag({
          objekt: plan.ref,
          handlung,
          akteur: args.akteur,
          von: `${status} bis ${faellig}`,
          nach: `${neuStatus} bis ${neuFaellig}`,
        }),
      ],
    },
  };
}



/**
 * Legt einen Kunden an — `PORTAL-002`, `04` §4.4.
 *
 * **Das Portal führt den Kundenstamm.** `04` §4.4 weist ihn keinem
 * Fremdsystem zu, und ein führendes System, in dem man den Datensatz nicht
 * anlegen kann, führt nichts: Bis hierher konnte die Oberfläche Kunden
 * anzeigen und durchsuchen, aber der Anruf eines Neukunden endete vor einer
 * Auswahlliste, in der er nicht stand.
 *
 * **`herkunft` ist `portal` und die externe Nummer bleibt leer.** Beide Felder
 * gibt es nach `PORTAL-002` von Anfang an, weil beim Kundenstamm noch offen
 * ist, ob die Branchensoftware ihn führt und die Akte ihn nur spiegelt. Wer
 * hier anlegt, legt im Portal an — und das steht dann auch so da, statt
 * später aus dem Fehlen einer Nummer erschlossen werden zu müssen.
 *
 * **Die Nummer vergibt der Betrieb nicht selbst.** Sie zählt vom höchsten
 * vorhandenen Wert hoch, wie bei `legeAuftragAn`. Eine frei eingetippte
 * Kundennummer wäre der kürzeste Weg zu zwei Akten mit derselben.
 */
export function legeKundenAn(args: {
  vorgaenge: Vorgaenge;
  bestand: Bestand;
  name: string;
  anschrift: Anschrift;
  akteur: ResourceRef;
  rollen: readonly Rolle[];
  kontakt?: Kontakt | undefined;
  notiz?: string | undefined;
}): Ausgang {
  if (!darfKundenPflegen(args.rollen)) {
    return {
      ok: false,
      grund:
        "Ihre Rolle pflegt den Kundenstamm nicht. Nach `07` §7.2 liegt er bei "
        + "Büro und Disposition.",
    };
  }

  if (!args.name.trim()) {
    return { ok: false, grund: "Ein Kunde ohne Namen ist später nicht wiederzufinden." };
  }

  /*
   * Der Ort gehört dazu, die Straße nicht zwingend.
   *
   * Ein Anruf bringt oft nur „Bäckerei Hufnagel in Burgebrach" — die genaue
   * Adresse kommt beim Einsatz. Ein Pflichtfeld dafür führt an dieser Stelle
   * zu erfundenen Straßen, und eine erfundene Adresse ist schlechter als eine
   * fehlende: Sie sieht aus, als stimme sie.
   */
  if (!args.anschrift.ort.trim()) {
    return { ok: false, grund: "Ohne Ort lässt sich kein Einsatz planen." };
  }

  const nummern = [
    ...args.bestand.kunden.map((k) => k.nummer),
    ...args.vorgaenge.neueKunden.map((k) => k.nummer),
  ]
    .map((n) => Number(n.replace(/^K-/, "")))
    .filter((n) => Number.isFinite(n));
  const naechste = `K-${Math.max(0, ...nummern) + 1}`;

  const kunde: Kunde = {
    ref: ref("portal", "customer", naechste.toLowerCase()),
    nummer: naechste,
    name: args.name.trim(),
    anschrift: args.anschrift,
    kontakte: args.kontakt ? [args.kontakt] : [],
    herkunft: "portal",
    seit: new Date().toISOString().slice(0, 10),
    ...(args.notiz?.trim() ? { notiz: args.notiz.trim() } : {}),
  };

  return {
    ok: true,
    vorgaenge: {
      ...args.vorgaenge,
      neueKunden: [...args.vorgaenge.neueKunden, kunde],
      audit: [
        ...args.vorgaenge.audit,
        eintrag({
          objekt: kunde.ref,
          handlung: `Kunde ${naechste} „${kunde.name}" angelegt`,
          akteur: args.akteur,
        }),
      ],
    },
  };
}

// ───────────────────────── Versionen und Papierkorb ─────────────────────────

/**
 * Holt eine Datei oder eine frühere Fassung zurück — `NC-003`.
 *
 * **Ohne `version` heißt es: aus dem Papierkorb.** Mit `version`: auf diesen
 * Stand zurücksetzen. Zwei Fälle, eine Funktion, weil beide dasselbe
 * Ergebnis haben — die Datei ist wieder da, wie sie einmal war — und dasselbe
 * protokolliert werden muss.
 *
 * Der Auditeintrag nennt **den Stand**, nicht nur die Datei. `05` §5.15 will
 * nachvollziehbar haben, was geschah; „Datei wiederhergestellt" beantwortet
 * nicht, welche Fassung jetzt gilt.
 */
export function holeZurueck(args: {
  vorgaenge: Vorgaenge;
  datei: ResourceRef;
  name: string;
  version?: string;
  stand?: string;
  akteur: ResourceRef;
}): Ausgang {
  const schluessel = formatResourceRef(args.datei);

  if (args.vorgaenge.entfernt.includes(schluessel)) {
    return {
      ok: false,
      grund: "Diese Datei wurde endgültig entfernt. Danach hilft nur die Sicherung.",
    };
  }

  return {
    ok: true,
    vorgaenge: {
      ...args.vorgaenge,
      zurueckgeholt: [...args.vorgaenge.zurueckgeholt, schluessel],
      audit: [
        ...args.vorgaenge.audit,
        eintrag({
          objekt: args.datei,
          handlung: args.version
            ? `„${args.name}" auf Fassung ${args.version} zurückgesetzt`
            : `„${args.name}" aus dem Papierkorb geholt`,
          akteur: args.akteur,
          ...(args.stand ? { von: args.stand } : {}),
        }),
      ],
    },
  };
}

/**
 * Entfernt eine Datei endgültig aus dem Papierkorb.
 *
 * **Nicht rückholbar**, und deshalb steht die Bestätigung in der Oberfläche
 * und die Regel hier: Eine Datei, die schon entfernt ist, lässt sich nicht
 * zweimal entfernen — der zweite Versuch bekommt eine Absage statt eines
 * zweiten Auditeintrags über etwas, das nicht geschah.
 */
export function entferneEndgueltig(args: {
  vorgaenge: Vorgaenge;
  datei: ResourceRef;
  name: string;
  akteur: ResourceRef;
}): Ausgang {
  const schluessel = formatResourceRef(args.datei);
  if (args.vorgaenge.entfernt.includes(schluessel)) {
    return { ok: false, grund: "Diese Datei ist bereits entfernt." };
  }

  return {
    ok: true,
    vorgaenge: {
      ...args.vorgaenge,
      entfernt: [...args.vorgaenge.entfernt, schluessel],
      audit: [
        ...args.vorgaenge.audit,
        eintrag({
          objekt: args.datei,
          handlung: `„${args.name}" endgültig entfernt`,
          akteur: args.akteur,
        }),
      ],
    },
  };
}

// ───────────────────────── Überlagerung ─────────────────────────

/**
 * Legt die Vorgänge über den Bestand und gibt einen neuen zurück.
 *
 * **Deshalb muss keine einzige Ansicht davon wissen.** Sie lesen weiter
 * `bestand.einsaetze` und `bestand.eingang`; was sie bekommen, ist der Stand
 * einschließlich dessen, was das Büro gerade entschieden hat. Der Alternative
 * — jede Ansicht fragt zusätzlich die Vorgänge ab — fehlt genau eine Stelle,
 * und dann zeigt eine Liste einen anderen Zustand als die Detailansicht
 * daneben.
 *
 * Der Auftragsstatus wird **mitgezogen**: Ein Auftrag, dessen erster Einsatz
 * losfährt, steht nicht mehr auf `geplant`. `04` §4.4 nennt die Statusfolge,
 * und ohne diese Ableitung bliebe die Auftragsliste stehen, während die
 * Einsätze weiterlaufen.
 */
export function ueberlagere(bestand: Bestand, vorgaenge: Vorgaenge): Bestand {
  const einsaetze = bestand.einsaetze.map((e) => {
    const neu = vorgaenge.einsatzzustand[formatResourceRef(e.ref)];
    return neu ? { ...e, zustand: neu } : e;
  });

  const eingang = bestand.eingang.map((v) => {
    const aenderung = vorgaenge.eingang[formatResourceRef(v.ref)];
    if (!aenderung) return v;
    return {
      ...v,
      status: aenderung.status,
      bearbeiter: aenderung.bearbeiter,
      ...(aenderung.zugeordneterAuftrag
        ? { zugeordneterAuftrag: aenderung.zugeordneterAuftrag }
        : {}),
    };
  });

  /**
   * **Ein Bericht, der aktualisiert wurde, ist derselbe Bericht.**
   *
   * Gemessen am 05.09.2026: Nach dem Übermitteln stand Version 2 zweimal in
   * der Akte — einmal als „Entwurf" aus dem Bestand, einmal als „Zur Prüfung"
   * aus dem geteilten Stand. Beide dieselbe Kennung, beide derselbe Einsatz.
   * Das Büro sah zwei Berichte und musste raten, welchen es prüft.
   *
   * Anhängen war richtig, solange ein Entwurf immer neu entstand. Seit die
   * Monteur-App einen vorhandenen Entwurf **aktualisiert** — mit gleicher
   * Kennung, weil `07` §7.16 die Freigabe an eine konkrete Version bindet —,
   * ist es falsch: Der neuere ersetzt den älteren, statt neben ihm zu stehen.
   *
   * Derselbe Fehler wie bei den MagicLinks eine Stunde vorher, an einer
   * anderen Stelle. Zusammenführen heißt entdoppeln, nicht aneinanderhängen.
   */
  const nachKennung = new Map<string, Servicebericht>();
  for (const b of [...bestand.berichte, ...vorgaenge.neueBerichte]) {
    nachKennung.set(formatResourceRef(b.ref), b);
  }

  const berichte = [...nachKennung.values()].map((b) => {
    const aenderung = vorgaenge.bericht[formatResourceRef(b.ref)];
    if (!aenderung) return b;
    return {
      ...b,
      zustand: aenderung.zustand,
      ...(aenderung.freigegebenVon ? { freigegebenVon: aenderung.freigegebenVon } : {}),
      ...(aenderung.freigegebenAm ? { freigegebenAm: aenderung.freigegebenAm } : {}),
    };
  });

  const wartungsplaene = bestand.wartungsplaene.map((p) => {
    const aenderung = vorgaenge.wartung[formatResourceRef(p.ref)];
    return aenderung
      ? { ...p, status: aenderung.status, naechsteFaelligkeit: aenderung.naechsteFaelligkeit }
      : p;
  });

  const chatbeitraege = [...bestand.chatbeitraege, ...vorgaenge.neueBeitraege];

  /**
   * Zurückgeholte Dateien verlieren ihre Löschmarkierung, endgültig entfernte
   * verschwinden.
   *
   * Aus der Liste **entfernt**, nicht mit einer zweiten Markierung versehen:
   * Nach `04` §4.2 bleibt eine gelöschte Ressource als Tombstone
   * referenzierbar, aber ein Papierkorb, in dem das endgültig Entfernte weiter
   * steht, ist keiner.
   */
  const ablage = bestand.ablage
    .filter((o) => !vorgaenge.entfernt.includes(formatResourceRef(o.ref)))
    .map((o) => {
      if (!vorgaenge.zurueckgeholt.includes(formatResourceRef(o.ref))) return o;
      // Die Löschmarkierung fällt weg. Ohne Destrukturierung, weil der Linter
      // die ungenutzten Bindungen sonst zu Recht anmahnt — und eine
      // Ausnahmeregel wäre teurer als eine Neuzuweisung.
      const rest = { ...o };
      delete (rest as { geloescht?: string }).geloescht;
      delete (rest as { geloeschtVon?: string }).geloeschtVon;
      return rest;
    });

  const magiclinks = [...bestand.magiclinks, ...vorgaenge.neueLinks].map((m) =>
    vorgaenge.widerrufen.includes(formatResourceRef(m.ref))
      ? { ...m, widerrufen: true }
      : m,
  );

  const kunden = [...bestand.kunden, ...vorgaenge.neueKunden];

  const auftraege = [...bestand.auftraege, ...vorgaenge.neueAuftraege].map((a) =>
    ableitenStatus(a, einsaetze),
  );

  return {
    ...bestand,
    auftraege,
    kunden,
    ablage,
    einsaetze,
    eingang,
    berichte,
    chatbeitraege,
    wartungsplaene,
    magiclinks,
    // Neueste zuletzt, wie im Bestand — die Ansichten sortieren selbst.
    audit: [...bestand.audit, ...vorgaenge.audit],
  };
}

/**
 * Leitet den Auftragsstatus aus seinen Einsätzen ab — `04` §4.4.
 *
 * Ein Auftrag ohne Einsätze bleibt, wie er ist: `neu` oder `geplant` sind
 * dann Aussagen des Büros, keine Ableitung. Sobald Einsätze existieren, gilt
 * der weiteste von ihnen — ein Auftrag, dessen zweiter Einsatz schon läuft,
 * ist nicht mehr `geplant`, auch wenn der dritte es noch ist.
 */
function ableitenStatus(auftrag: Auftrag, einsaetze: Bestand["einsaetze"]): Auftrag {
  const meine = einsaetze.filter(
    (e) => formatResourceRef(e.auftrag) === formatResourceRef(auftrag.ref),
  );
  if (meine.length === 0) return auftrag;

  const zustaende = new Set(meine.map((e) => e.zustand));
  const laufend = ["anfahrt", "vor-ort", "wartet", "abschluss", "uebermittelt"];

  if (meine.every((e) => e.zustand === "storniert")) {
    return { ...auftrag, status: "storniert" };
  }
  if (meine.every((e) => e.zustand === "geprueft" || e.zustand === "storniert")) {
    return { ...auftrag, status: "geprueft" };
  }
  if (laufend.some((z) => zustaende.has(z as EinsatzZustand))) {
    return { ...auftrag, status: "in-arbeit" };
  }
  if (meine.some((e) => e.monteur !== undefined)) {
    return { ...auftrag, status: "zugewiesen" };
  }
  return { ...auftrag, status: "geplant" };
}
